import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardHead } from '../../design-system/composites/Card/Card';
import { WarningIcon } from '@phosphor-icons/react/dist/csr/Warning';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';

import { Button } from '../../design-system/primitives/Button/Button';
import { escalationsApi, type EscalationRow } from '../../lib/api/escalations';
import { STATUS_LABEL_KEY } from '../escalations/escalationLabels';
import { formatTimestamp } from '../../lib/format/dateTime';
import styles from './CaseEscalations.module.css';

/**
 * The Escalations panel on the case detail screen (M-10, Task 6, spec §3) —
 * beside M-08's `CaseMessages` and M-09's `CaseRequests`, the identical
 * shape: gated on its own permission, its own error state, no spinner that
 * never resolves.
 *
 * ## `status: 'all'` IS LOAD-BEARING (Global Constraint, this task's own brief)
 *
 * `CaseRequests.tsx`'s own docblock records the exact defect this panel
 * must not repeat: M-09's identical panel took the server's default queue
 * (`open`, which for escalations means "awaiting the current level's
 * assignee" — `EscalationController::index()`'s own comment) and rendered
 * "no requests" for every case on a live system, because a row created a
 * moment ago is never in that bucket. Omitting `status` here would
 * reproduce the identical defect for escalations: a `resolved`/`closed`
 * complaint about this case would silently vanish from its own record. This
 * panel therefore always passes `status: 'all'`, verified by
 * `CaseEscalations.test.tsx`'s own assertion on the outgoing query, not
 * merely on a row happening to be present.
 *
 * ## Filtered by `case_number`, not the case's `uuid`
 *
 * `EscalationIndexRequest::rules()` (verified against the API repository)
 * carries a `case_number` filter and no `case_uuid` filter at all — the
 * identical shape `CaseRequests.tsx`'s own docblock explains for
 * `case_number` over an invented `case_uuid`. `CaseDetail.tsx` already
 * holds the case's own `case_number`, unique per case, so this panel
 * filters on that rather than adding a filter out of this task's scope.
 *
 * ## Read-only, deliberately
 *
 * No advance/resolve/close/comment controls here — `EscalationDetail.tsx`
 * (Task 5) is where those live, gated on their own permission and driven by
 * the record's own `status`/`level`, never re-derived a second time on this
 * surface. This panel answers one question only: which escalations concern
 * this case, and where each currently stands.
 */

export interface CaseEscalationsProps {
  caseNumber: string;
  canView: boolean;
}

type LoadStatus = 'loading' | 'ready' | 'error';

export function CaseEscalations({ caseNumber, canView }: CaseEscalationsProps) {
  const { t, i18n } = useTranslation();

  const [status, setStatus] = useState<LoadStatus>('loading');
  const [rows, setRows] = useState<readonly EscalationRow[] | null>(null);

  const loadEscalations = useCallback(async () => {
    setStatus('loading');

    try {
      // `status: 'all'` IS LOAD-BEARING — see this module's own docblock.
      const page = await escalationsApi.list({
        case_number: caseNumber,
        status: 'all',
        per_page: 200,
      });
      setRows(page.data);
      setStatus('ready');
    } catch {
      // This panel's own error state, not `CaseDetail`'s — see this
      // module's own docblock.
      setStatus('error');
    }
  }, [caseNumber]);

  useEffect(() => {
    if (!canView) return;
    void loadEscalations();
  }, [canView, loadEscalations]);

  if (!canView) return null;

  return (
    <section className={styles['panel']}>
      <CardHead level="h3" icon={<WarningIcon size={16} weight="duotone" />} title={t('escalations.panelTitle')} />

      {status === 'error' ? (
        <div role="alert" className={styles['empty']}>
          <p>{t('escalations.loadFailed')}</p>
          <Button
            variant="secondary"
            onClick={() => {
              void loadEscalations();
            }}
            icon={<ArrowClockwiseIcon size={16} />}
          >
            {t('cases.retry')}
          </Button>
        </div>
      ) : rows === null ? (
        <p role="status">{t('list.loading')}</p>
      ) : rows.length === 0 ? (
        <p className={styles['empty']}>{t('escalations.noneForCase')}</p>
      ) : (
        <ul className={styles['list']}>
          {rows.map((escalation) => (
            <li key={escalation.uuid} className={styles['row']}>
              <div className={styles['rowHeader']}>
                <span className={styles['mobile']}>
                  <bdi dir="ltr">{escalation.mobile}</bdi>
                </span>
                <span className={styles['statusLabel']}>{t(STATUS_LABEL_KEY[escalation.status])}</span>
              </div>

              <div className={styles['meta']}>
                <span>{t('escalations.level')}: {escalation.level}</span>
                {escalation.assignee_name === null ? null : (
                  <span>{t('escalations.assignedTo')}: {escalation.assignee_name}</span>
                )}
                {escalation.opened_at === null ? null : (
                  <span>
                    {t('escalations.openedAt')}: <bdi dir="ltr">{formatTimestamp(escalation.opened_at, i18n.language)}</bdi>
                  </span>
                )}
              </div>

              {escalation.reason === null ? null : <p className={styles['reason']}>{escalation.reason}</p>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
